import React, { createContext, useState, useContext, ReactNode, useEffect } from 'react';
import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
  GoogleAuthProvider,
  signInWithPopup,
  getIdToken,
  getAdditionalUserInfo
} from 'firebase/auth';
import { auth } from '../config/firebase';

interface User {
  id: string;
  email: string | null;
  name: string;
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (name: string, email: string, password: string) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {},
  signup: async () => {},
  loginWithGoogle: async () => {},
  logout: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const formatUser = (firebaseUser: FirebaseUser): User => ({
  id: firebaseUser.uid,
  email: firebaseUser.email,
  name: firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
});

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        setUser(formatUser(firebaseUser));
      } else {
        setUser(null);
      }
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Helper function to send the welcome email
  const sendWelcomeEmail = async (email: string, token: string) => {
    try {
      await fetch('https://remindme-planet.appspot.com/welcome_email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ email }),
      });
      // Optional: Log success for welcome email
      console.log(`Welcome email request sent for ${email}`);
    } catch (emailError) {
      // Log the error for sending the welcome email, but don't let it fail the signup/login.
      console.error('Failed to send welcome email:', emailError);
    }
  };

  const login = async (email: string, password: string) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      setUser(formatUser(userCredential.user));
    } catch (error) {
      throw new Error(`Invalid credentials ${error}`);
    }
  };

  const signup = async (name: string, email: string, password: string) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      // setUser(formatUser(userCredential.user)); // Rely on onAuthStateChanged to update user state
      
      // After successful user creation, call the welcome email endpoint
      if (userCredential.user && userCredential.user.email) {
        const token = await getIdToken(userCredential.user);
        await sendWelcomeEmail(userCredential.user.email, token);
      }
    } catch (error) {
      throw new Error(`Failed to create account: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const loginWithGoogle = async () => {
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      // setUser(formatUser(result.user)); // Rely on onAuthStateChanged

      // Check if the user is new
      const additionalUserInfo = getAdditionalUserInfo(result);
      if (additionalUserInfo?.isNewUser && result.user && result.user.email) {
        const token = await getIdToken(result.user);
        await sendWelcomeEmail(result.user.email, token);
      }
    } catch (error) {
      throw new Error(`Failed to sign in with Google: ${error instanceof Error ? error.message : String(error)}`);
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      setUser(null);
    } catch (error) {
      console.error('Failed to log out:', error);
      throw new Error('Failed to log out');
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        signup,
        loginWithGoogle,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};